import * as React from 'react'
import { useEffect, useState } from 'react'

import { FULL, restrictedPage, mapItemsIfNotAll } from '../shared';
import { Snackbar, defaultSnackbarTimeout, getPageTitle, Spinner, DropdownInput, SegmentedControl, filterIcon, EXTRA_SMALL_ICON_BASE_SRC, } from '../../Shared/index'
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor'
import SnapshotSection from '../components/usage_snapshots/snapshotSection'
import { snapshotSections, TAB_NAMES, ALL, SECTION_NAME_TO_ICON_URL, } from '../components/usage_snapshots/shared'
import useWindowSize from '../../Shared/hooks/useWindowSize';
import { requestDelete, requestGet, requestPost, } from '../../../modules/request';
import ReportSubscriptionModal from '../components/usage_snapshots/reportSubscriptionModal';

const MAX_VIEW_WIDTH_FOR_MOBILE = 950
const PDF_REPORT = 'usage_snapshot_report_pdf'

export const UsageSnapshotsContainer = ({
  accessType,
  loadingFilters,
  customStartDate,
  customEndDate,
  pusherChannel,
  searchCount,
  selectedClassrooms,
  availableClassrooms,
  selectedGrades,
  availableGrades,
  selectedSchools,
  selectedTeachers,
  availableTeachers,
  selectedTimeframe,
  saveFilterSelections,
  openMobileFilterMenu,
  horizontalFilters
}) => {
  document.title = getPageTitle("Premium Hub", "Premium Reports | Usage Snapshot Report")
  const [selectedTab, setSelectedTab] = useState(ALL)
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false)
  const [currentPdfSubscription, setCurrentPdfSubscription] = useState(null)
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false)
  const [snackbarCopy, setSnackbarCopy] = useState('')

  useSnackbarMonitor(isSnackbarVisible, setIsSnackbarVisible, defaultSnackbarTimeout)

  useEffect(() => {
    requestGet(`/pdf_subscriptions/current?report=${PDF_REPORT}`, (body) => {
      setCurrentPdfSubscription(body)
    })
  }, [])

  const size = useWindowSize()

  const showSnackbar = (snackbarCopy: string) => {
    setSnackbarCopy(snackbarCopy)
    setIsSnackbarVisible(true)
  }

  function handleClickDownloadReport() { window.print() }

  function handleSetSelectedTabFromDropdown(option) { setSelectedTab(option.value) }

  function handleTabChange(e) { setSelectedTab(e.currentTarget.value) }

  function handleClickSubscribe() {
    setIsSubscriptionModalOpen(!isSubscriptionModalOpen);
  }

  function handleSubscriptionCancel() {
    setIsSubscriptionModalOpen(false);
  }

  function handleSubscriptionSave(isSubscribed, frequency) {
    setIsSubscriptionModalOpen(false);
    if (isSubscribed) {
      saveFilterSelections(PDF_REPORT, (adminReportFilterSelection) => {
        createOrUpdatePdfSubscription(adminReportFilterSelection, frequency)
      })
    } else if (currentPdfSubscription) {
      deletePdfSubscription(currentPdfSubscription.id)
    }
    showSnackbar('Subscription settings saved')
  }

  function createOrUpdatePdfSubscription(adminReportFilterSelection, frequency) {
    if (!adminReportFilterSelection?.id) { return }

    const pdfSubscriptionParams = {
      pdf_subscription: {
        admin_report_filter_selection_id: adminReportFilterSelection.id,
        frequency
      }
    }

    requestPost('/pdf_subscriptions/create_or_update', pdfSubscriptionParams, (pdfSubscription) => {
      setCurrentPdfSubscription(pdfSubscription)
    })
  }

  function deletePdfSubscription(pdfSubscriptionId) {
    requestDelete(`/pdf_subscriptions/${pdfSubscriptionId}`, {}, () => setCurrentPdfSubscription(null), error => { throw (error) })
  }

  if (loadingFilters) {
    return <Spinner />
  }

  const renderSnackbar = () => {
    return <Snackbar text={snackbarCopy} visible={isSnackbarVisible} />
  }

  const segmentedControlButtons = TAB_NAMES.map(tabName => ({
    label: tabName,
    inactiveIconSrc: SECTION_NAME_TO_ICON_URL[tabName],
    activeIconSrc: SECTION_NAME_TO_ICON_URL[tabName],
    onClick: handleTabChange
  }))

  const tabDropdownOptions = TAB_NAMES.map(tab => ({ value: tab, label: tab, }))

  const tabDropdown = (
    <DropdownInput
      ariaLabel="Select tab"
      handleChange={handleSetSelectedTabFromDropdown}
      id="tab-dropdown"
      isSearchable={false}
      options={tabDropdownOptions}
      value={tabDropdownOptions.find(opt => opt.value === selectedTab)}
    />
  )

  const sectionsToShow = selectedTab === ALL ? snapshotSections : snapshotSections.filter(s => s.name === selectedTab)
  const snapshotSectionComponents = sectionsToShow.map(section => (
    <SnapshotSection
      active={section.name === selectedTab}
      className={section.className}
      customTimeframeEnd={customEndDate}
      customTimeframeStart={customStartDate}
      itemGroupings={section.itemGroupings}
      key={section.name}
      name={section.name}
      pusherChannel={pusherChannel}
      searchCount={searchCount}
      selectedClassroomIds={mapItemsIfNotAll(selectedClassrooms, availableClassrooms)}
      selectedGrades={mapItemsIfNotAll(selectedGrades, availableGrades, 'value')}
      selectedSchoolIds={selectedSchools.map(s => s.id)}
      selectedTeacherIds={mapItemsIfNotAll(selectedTeachers, availableTeachers)}
      selectedTimeframe={selectedTimeframe.value}
    />
  ))

  if (accessType !== FULL) {
    return restrictedPage
  }

  return (
    <main>
      <div className="header">
        <h1>
          Usage Snapshot Report
        </h1>
        <div className="header-buttons">
          <a className="quill-button outlined small grey focus-on-light" href="https://support.quill.org/en/articles/8358350-how-do-i-use-the-usage-snapshot-report" rel="noopener noreferrer" target="_blank">
            <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`} />
            <span>Guide</span>
          </a>
          <button
            className="quill-button outlined small grey focus-on-light"
            onClick={handleClickSubscribe}
            type="button"
          >
            <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/email.svg`} />
            <span>Subscribe</span>
          </button>
          <button className="quill-button outlined small grey focus-on-light" onClick={handleClickDownloadReport} type="button">
            <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />
            <span>Download</span>
          </button>
        </div>
      </div>
      {size.width >= MAX_VIEW_WIDTH_FOR_MOBILE ? (
        <div className="usage-snapshots-segmented-control">
          <SegmentedControl
            activeTab={selectedTab}
            buttons={segmentedControlButtons}
            label="Report view:"
            size="small"
          />
        </div>
      ) : tabDropdown}
      {horizontalFilters}
      <div className="filter-button-container">
        <button
          className="interactive-wrapper focus-on-light"
          onClick={openMobileFilterMenu}
          type="button"
        >
          <img alt={filterIcon.alt} src={filterIcon.src} />
          Filters
        </button>
      </div>
      <div className="sections">
        {snapshotSectionComponents}
      </div>
      <ReportSubscriptionModal
        cancel={handleSubscriptionCancel}
        currentSubscription={currentPdfSubscription}
        isOpen={isSubscriptionModalOpen}
        save={handleSubscriptionSave}
      />
      {renderSnackbar()}
      <div id="bottom-element" />

    </main >
  )
}

export default UsageSnapshotsContainer;
